import { useFormik } from "formik";
import moment from "moment/moment";
import React, { useEffect, useMemo, useState } from "react";
import { BsPencilFill } from "react-icons/bs";
import { ImBin } from "react-icons/im";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import Select from "../../../components/form/select/Select";
import EditRiskCovered from "../../../components/modals/policy/EditRiskCovered";
import PageTitle from "../../../components/partials/PageTitle";
import {
  allActivePolicies,
  attachPolicy,
  deleteRiskCover,
  getAllActivePolicies,
  getSingleAppliedPolicyCovers,
  getSingleAppliedPolicyDetails,
  // resetActivePolicies,
  resetPolicyStatus,
  resetRiskCover,
} from "../../../redux/slice/policy/policySlice";
import { categories } from "../../../utils/data/account_type";
import { flashMessage } from "../../../utils/flash";
import { removeLeadingZeros } from "../../../utils/helpers/filter";
import { policyApplication } from "../../../utils/validators/schema";
import { policyValidation } from "../../../utils/validators/validators";

const EditAppliedPolicy = () => {
  const activePolicies = useSelector(allActivePolicies);
  const {
    single_policy,
    single_policy_cover,
    risk_covered,
    //   isLoading,
    temp_policy_attach_data,
    isUpdated,
    //   isSuccess,
  } = useSelector((state) => state.policy);
  const { user } = useSelector((state) => state?.auth?.user);
  const { user_permissions } = useSelector((state) => state?.auth?.user);
  const { single_customer } = useSelector((state) => state?.customer);
  const { all_covers } = useSelector((state) => state?.cover);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [butCharge, setButCharge] = useState(
    temp_policy_attach_data?.but_charge ? true : false
  );
  const [riskCovered, setRiskCovered] = useState(
    single_policy?.policy_risk_info || temp_policy_attach_data?.risk_covered
  );
  const [editData, setEditData] = useState(null);

  const [endDate, setEndDate] = useState(
    single_policy?.policy_app_basic_info?.end_date
  );
  const [policyId, setPolicyId] = useState(
    single_policy?.policy_risk_info[0]?.policy_uuid
  );
  const [policyIdError, setPolicyIdError] = useState({
    error: "",
    touched: false,
  });
  const [dbRiskCovers, setDbRiskCovers] = useState(null);
  const [riskError, setRiskError] = useState("");
  const [applyTo, setApplyTo] = useState(() => {
    let applyTo;
    if (temp_policy_attach_data) {
      if (temp_policy_attach_data?.repayment_frequency) {
        applyTo = "OO";
      } else {
        applyTo = "INS";
      }
    }
    return applyTo;
  });

  const handleEditData = (value) => {
    const indexOfRisk = dbRiskCovers?.findIndex((item) => item.id === value.id);
    let data = [...dbRiskCovers];
    if (indexOfRisk !== -1) {
      data[indexOfRisk] = value;
      setDbRiskCovers(data);
    }
  };

  const onSubmit = (values) => {
    let policyIdCopy = temp_policy_attach_data?.policy_id
      ? temp_policy_attach_data?.policy_id
      : policyId;

    if (
      parseFloat(single_policy?.policy_app_basic_info?.total_annual_premium) ===
      parseFloat(single_policy?.payment_amount)
    ) {
      flashMessage(
        "error",
        "This policy is fully paid. You cannot edit a fully paid policy"
      );
      return;
    }

    if (!policyIdCopy) {
      setPolicyIdError({
        error: "Policy is required",
        touched: true,
      });
      return;
    }

    let payload = {
      ...values,
      policy_app_no: state,
      end_date: endDate,
      customer_id: single_policy?.policy_app_basic_info?.customer_id,
      risk_covered: riskCovered.concat(dbRiskCovers),
      policy_id: policyIdCopy,
      policyName: activePolicies?.policy?.filter(
        (item) =>
          item?.id === parseInt(single_policy?.policy_risk_info[0]?.policy_uuid)
      )[0]?.name,
      edit: true,
    };
    dispatch(attachPolicy(payload));
    setIsSubmitted(true);
  };

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    setFieldTouched,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      duration:
        temp_policy_attach_data?.duration ||
        single_policy?.policy_app_basic_info?.policy_aplication_duration,
      effective_date: moment(
        single_policy?.policy_app_basic_info?.effective_date
      ).format("YYYY-MM-DD"),
      repayment_frequency: temp_policy_attach_data?.repayment_frequency
        ? temp_policy_attach_data?.repayment_frequency
        : single_policy?.policy_app_basic_info?.repayment_frequency,
      discount:
        temp_policy_attach_data?.discount ||
        single_policy?.policy_app_basic_info?.discount,
      but_charge:
        temp_policy_attach_data?.but_charge ||
        single_policy?.policy_app_basic_info?.but_charge,
      total_annual_premium: null,
    },
    validationSchema: policyApplication,
    validate: (values) => policyValidation(values, butCharge),
    onSubmit,
  });

  // date manipulation starts here
  const calculateEndDate = () => {
    const effectiveDateObj = moment(values?.effective_date);
    const EndDateObj = effectiveDateObj
      .add(values?.duration, "months")
      .subtract(1, "days"); // Subtract 1 day
    const EndDateFormatted = EndDateObj.format("YYYY-MM-DD");
    setEndDate(EndDateFormatted);
  };

  const totalPremium = useMemo(() => {
    let totalPremium = 0;
    if (riskCovered?.length > 0 || dbRiskCovers?.length > 0) {
      let risks = dbRiskCovers?.concat(riskCovered);
      let total = risks?.reduce((acc, totalPremium) => {
        let calc =
          parseFloat(totalPremium?.sum_insured) *
          parseFloat(totalPremium.rate) *
          0.01;
        return (acc += calc);
      }, 0);
      totalPremium = total;
    }
    if (values.discount) {
      totalPremium -= totalPremium * values.discount * 0.01;
    }
    setFieldValue("total_annual_premium", totalPremium);
    return totalPremium;
  }, [values.discount, riskCovered, dbRiskCovers]);

  useEffect(() => {
    dispatch(getSingleAppliedPolicyDetails(state));
    if (!temp_policy_attach_data) {
      dispatch(resetRiskCover());
    }
  }, [dispatch, state]);

  useEffect(() => {
    if (values?.effective_date || values?.duration) {
      calculateEndDate();
    }
  }, [values?.effective_date, values?.duration]);

  useEffect(() => {
    if (!temp_policy_attach_data) {
      dispatch(getAllActivePolicies({ office_id: user.office_id }));
      dispatch(resetPolicyStatus());
    }
    if (single_policy) {
      setPolicyId(single_policy?.policy_risk_info[0]?.policy_uuid);
      dispatch(
        getSingleAppliedPolicyCovers(
          parseInt(single_policy?.policy_risk_info[0]?.policy_uuid)
        )
      );
      setDbRiskCovers(single_policy?.policy_risk_info);
      setApplyTo(
        single_policy?.policy_app_basic_info?.repayment_frequency === "one-off"
          ? "OO"
          : "INS"
      );
      setRiskCovered(
        temp_policy_attach_data?.risk_covered?.filter(
          (item) => item.policy_application_number === undefined
        )
      );
    }
  }, [single_policy, temp_policy_attach_data]);

  useEffect(() => {
    if (single_policy) {
      setRiskCovered(risk_covered);
      setRiskError("");
    }
  }, [risk_covered, single_policy]);

  useEffect(() => {
    if (isSubmitted) {
      navigate(`/admin/policy-application/overview`);
    }
  }, [isSubmitted, navigate]);

  useEffect(() => {
    if (
      !user_permissions?.some(
        (permission) => permission.name === "customer module"
      ) ||
      !user_permissions?.some(
        (permission) => permission.name === "policy module"
      ) ||
      !user_permissions?.some(
        (permission) => permission.name === "edit applied policy"
      )
    ) {
      navigate("/admin/dashboard");
    }
    // if (!state?.basic_information) {
    //   navigate("/admin/dashboard");
    // }
  }, [navigate, state?.basic_information, user_permissions]);

  const breadcrumb = {
    from: {
      title: "Applied policies",
      link: "/admin/all-applied-general-policies",
    },
    current: {
      title: "policy edit",
      link: null,
    },
  };

  if (single_policy && single_policy_cover) {
    return (
      <>
        {/* page title */}
        <PageTitle title='Applied Policy Edit' breadcrumb={breadcrumb} />
        {/* end page title */}

        <div className='row'>
          <div className='col-md-12'>
            <div className='card'>
              <div className='card-body'>
                <div>
                  <div className='row'>
                    <div className='col-md-12'>
                      <div className='mb-3'>
                        <label htmlFor='policy_id' className='form-label'>
                          Select Policy <span className='text-danger'>*</span>
                        </label>
                        <input
                          type='text'
                          readOnly
                          className='form-control form-control-sm bg-white '
                          defaultValue={
                            activePolicies?.policy?.filter(
                              (item) =>
                                item?.id ===
                                parseInt(
                                  single_policy?.policy_risk_info[0]
                                    ?.policy_uuid
                                )
                            )[0]?.name
                          }
                        />
                      </div>
                    </div>
                    <div className='col-md-12 mb-3'>
                      <div className='col-md-4'>
                        <span
                          className='btn btn-primary text-white px-4'
                          data-bs-toggle='modal'
                          data-bs-target='#more_document_modal'
                        >
                          Add Risk Covered
                        </span>
                      </div>
                    </div>
                    <form onSubmit={handleSubmit} className='row'>
                      {/* risk covered table */}

                      {riskCovered?.length > 0 || dbRiskCovers?.length > 0 ? (
                        <div className='col-md-12 mb-3 mx-2 table-responsive-sm'>
                          <table className='table table-centered table-striped nowrap w-100 no-footer'>
                            <thead>
                              <tr>
                                <th
                                  className='sorting'
                                  tabIndex='0'
                                  rowSpan='1'
                                  colSpan='1'
                                  style={{ width: "130.8px" }}
                                  aria-label='Customer: activate to sort column ascending'
                                >
                                  Policy Cover Type
                                </th>
                                <th
                                  className='sorting'
                                  tabIndex='0'
                                  rowSpan='1'
                                  colSpan='1'
                                  style={{ width: "140.8px" }}
                                  aria-label='Customer: activate to sort column ascending'
                                >
                                  Cover For
                                </th>
                                <th
                                  className='sorting'
                                  tabIndex='0'
                                  rowSpan='1'
                                  colSpan='1'
                                  style={{ width: "108.8px" }}
                                  aria-label='Phone: activate to sort column ascending'
                                >
                                  Rate(%)
                                </th>
                                <th
                                  className='sorting'
                                  tabIndex='0'
                                  rowSpan='1'
                                  colSpan='1'
                                  style={{ width: "147.8px" }}
                                >
                                  Sum Insured
                                </th>
                                <th
                                  className='sorting'
                                  tabIndex='0'
                                  rowSpan='1'
                                  colSpan='1'
                                  style={{ width: "147.8px" }}
                                  aria-label='Email: activate to sort column ascending'
                                >
                                  Annual Premium
                                </th>
                                <th
                                  className='sorting'
                                  tabIndex='0'
                                  rowSpan='1'
                                  colSpan='1'
                                  style={{ width: "246.8px" }}
                                  aria-label='Location: activate to sort column ascending'
                                >
                                  Detail Description / Specification
                                </th>
                                <th
                                  className='sorting sorting_asc'
                                  tabIndex='0'
                                  rowSpan='1'
                                  colSpan='1'
                                  style={{ width: "146.8px" }}
                                ></th>
                              </tr>
                            </thead>

                            <tbody>
                              {dbRiskCovers?.map((item) => (
                                <tr key={item?.policy_application_number}>
                                  <td>
                                    {
                                      all_covers?.unpaginated_cover?.filter(
                                        (p) => String(p.id) === item?.cover_type
                                      )[0]?.name
                                    }
                                  </td>
                                  <td>{item?.cover_for}</td>
                                  <td className='table-user'>{item?.rate}</td>
                                  <td className='table-user'>
                                    {item?.sum_insured}
                                  </td>
                                  <td className='text-dark'>
                                    {parseFloat(item?.annual_premium)?.toFixed(
                                      2
                                    )}
                                  </td>
                                  <td>{item?.description}</td>

                                  <td className='text-center'>
                                    <div className='d-flex align-content-center justify-content-end gap-2'>
                                      <span
                                        className='text-primary cursor-p'
                                        data-bs-toggle='modal'
                                        data-bs-target='#more_document_modal'
                                        onClick={() => setEditData(item)}
                                      >
                                        <BsPencilFill size={17} />
                                      </span>

                                      {!item?.policy_application_number ? (
                                        <span
                                          className='text-danger cursor-p'
                                          onClick={() =>
                                            dispatch(deleteRiskCover(item?.id))
                                          }
                                        >
                                          <ImBin size={17} />
                                        </span>
                                      ) : (
                                        <span className='p-1'></span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                              {riskCovered?.map((item, i) => (
                                <tr key={i}>
                                  <td>
                                    {
                                      // single_policy_cover?.policy_cover?.filter(
                                      //   (p) =>
                                      //     parseInt(p.id) ===
                                      //     parseInt(item?.cover_type)
                                      // )[0]?.cover
                                      all_covers?.unpaginated_cover?.filter(
                                        (p) => p.id === item?.cover_type
                                      )[0]?.name
                                    }
                                  </td>
                                  <td>{item?.cover_for}</td>
                                  <td className='table-user'>{item?.rate}</td>
                                  <td className='table-user'>
                                    {item?.sum_insured}
                                  </td>
                                  <td className='text-dark'>
                                    {parseFloat(item?.annual_premium)?.toFixed(
                                      2
                                    )}
                                  </td>
                                  <td>{item?.description}</td>

                                  <td className='text-center'>
                                    <div className='d-flex align-content-center justify-content-end gap-2'>
                                      <span
                                        className='text-primary cursor-p'
                                        data-bs-toggle='modal'
                                        data-bs-target='#more_document_modal'
                                        onClick={() => setEditData(item)}
                                      >
                                        <BsPencilFill size={17} />
                                      </span>

                                      {!item?.policy_application_number ? (
                                        <span
                                          className='text-danger cursor-p'
                                          onClick={() =>
                                            dispatch(deleteRiskCover(item?.id))
                                          }
                                        >
                                          <ImBin size={17} />
                                        </span>
                                      ) : (
                                        <span className='p-1'></span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                      {riskError && (
                        <div>
                          <h6 className='alert alert-danger mb-4'>
                            {riskError}
                          </h6>
                        </div>
                      )}
                      {/* end risk covered table */}
                      <div className='col-md-4'>
                        <div className='mb-3'>
                          <label htmlFor='policy_name' className='form-label'>
                            Duration (in months){" "}
                            <span className='text-danger'>*</span>
                          </label>
                          <input
                            type='number'
                            id='duration'
                            name='duration'
                            className={`form-control form-control-sm no-arrows`}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values?.duration}
                            onInput={(e) => removeLeadingZeros(e.target.id)}
                            // placeholder='Enter Duration'
                          />
                          {errors?.duration && touched?.duration && (
                            <span className='text-danger'>
                              {errors?.duration}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div className='mb-3'>
                          <label htmlFor='policy_name' className='form-label'>
                            Effective date{" "}
                            <span className='text-danger'>*</span>
                          </label>
                          <input
                            type='date'
                            id='effective_date'
                            name='effective_date'
                            className={`form-control form-control-sm`}
                            value={values?.effective_date}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors?.effective_date &&
                            touched?.effective_date && (
                              <span className='text-danger'>
                                {errors?.effective_date}
                              </span>
                            )}
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div className='mb-3'>
                          <label htmlFor='policy_name' className='form-label'>
                            End date
                          </label>
                          <input
                            type='date'
                            id='end_date'
                            name='end_date'
                            value={endDate}
                            className={`form-control form-control-sm`}
                            disabled
                          />
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div className='mb-3'>
                          <label className='form-label'>
                            Repayment Frequency{" "}
                            <span className='text-danger'>*</span>
                          </label>
                          <div className='form-check my-1'>
                            <input
                              className='form-check-input me-2'
                              type='radio'
                              name='apply_to'
                              id='apply_to'
                              onChange={(e) => {
                                setApplyTo("OO");
                                // setApplyTo(e.target.value);
                                setFieldValue("repayment_frequency", "one-off");
                              }}
                              checked={applyTo === "OO"}
                            />
                            <label
                              className='form-check-label'
                              htmlFor='apply_to'
                            >
                              One-off
                            </label>
                          </div>
                          <div className='form-check'>
                            <input
                              className='form-check-input me-2'
                              type='radio'
                              name='apply_to'
                              id='apply_to'
                              checked={applyTo === "INS"}
                              onChange={() => {
                                setApplyTo("INS");
                                setFieldValue("repayment_frequency", "");
                                setFieldTouched("repayment_frequency", false);
                              }}
                            />
                            <label
                              className='form-check-label mb-1'
                              htmlFor='repayment_frequency'
                            >
                              Installments
                            </label>
                            {applyTo === "INS" && (
                              <div>
                                <Select
                                  isError={
                                    errors?.repayment_frequency &&
                                    touched?.repayment_frequency
                                  }
                                  name='repayment_frequency'
                                  // ref={repayment_frequencyRef}
                                  data={categories?.map((item) => ({
                                    label: item?.name,
                                    value: item?.name,
                                  }))}
                                  value={values.repayment_frequency}
                                  onChange={(selectedOption) =>
                                    setFieldValue(
                                      "repayment_frequency",
                                      selectedOption?.value
                                    )
                                  }
                                  onBlur={() => {
                                    handleBlur({
                                      target: { name: "repayment_frequency" },
                                    });
                                  }}
                                />
                                {errors?.repayment_frequency &&
                                  touched?.repayment_frequency && (
                                    <p className='text-danger mb-0'>
                                      {errors?.repayment_frequency}
                                    </p>
                                  )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div className='mb-3'>
                          <label htmlFor='policy_name' className='form-label'>
                            Discount(%)
                          </label>
                          <input
                            type='number'
                            id='discount'
                            name='discount'
                            value={values?.discount}
                            className={`form-control form-control-sm no-arrows`}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors?.discount && touched?.discount && (
                            <span className='text-danger'>
                              {errors?.discount}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div className='mb-3'>
                          <label
                            htmlFor='but_charge'
                            className='form-label d-flex gap-1 align-content-center'
                          >
                            <input
                              className={`form-check-input form-check-input-sm`}
                              type='checkbox'
                              id='but_charge'
                              onChange={() => {
                                setButCharge(!butCharge);
                                setFieldValue("but_charge", null);
                              }}
                              style={{ height: "16px", width: "16px" }}
                              defaultChecked={
                                temp_policy_attach_data?.but_charge
                              }
                            />
                            But-Charge
                          </label>
                          {butCharge && (
                            <input
                              type='number'
                              id='but_charge'
                              name='but_charge'
                              className={`form-control form-control-sm no-arrows`}
                              value={values?.but_charge}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              onInput={(e) => removeLeadingZeros(e.target.id)}
                            />
                          )}
                          {errors?.but_charge &&
                            touched?.but_charge &&
                            butCharge && (
                              <span className='text-danger'>
                                {errors?.but_charge}
                              </span>
                            )}
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div className='mb-3'>
                          <label htmlFor='policy_name' className='form-label'>
                            Total Annual Premium
                          </label>
                          <input
                            type='number'
                            id='total_premium'
                            name='total_premium'
                            className={`form-control form-control-sm`}
                            value={
                              totalPremium > 0 ? totalPremium?.toFixed(2) : 0
                            }
                            readOnly
                          />
                        </div>
                      </div>
                      <div className='d-flex justify-content-end pt-5'>
                        {/* <button
                                      type='button'
                                      className='btn btn-info text-white me-3'
                                      onClick={() => accountingRef?.current?.click()}
                                  >
                                      <i className='mdi mdi-arrow-left me-1'></i>
                                      Previous
                                  </button> */}
                        <button
                          type='submit'
                          className='btn btn-primary text-white px-4'
                        >
                          Next
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <EditRiskCovered
          modal_id='more_document_modal'
          title={`${editData ? "Edit Risk Covered" : "Add Risk Covered"}`}
          editData={editData}
          setEditData={setEditData}
          selectedRisks={dbRiskCovers?.concat(riskCovered)}
          handleEditData={handleEditData}
        />
      </>
    );
  } else {
    return (
      <>
        {/* page title */}
        <PageTitle title='Applied Policy Edit' breadcrumb={breadcrumb} />
        {/* end page title */}
        <BeatLoader
          height='80'
          width='80'
          radius='9'
          color='#2e3192'
          ariaLabel='three-dots-loading'
          wrapperStyle={{}}
          wrapperClassName=''
          visible={true}
          // className='mt-3'
        />
      </>
    );
  }
};

export default EditAppliedPolicy;
